import './App.css';

import { Security } from '@okta/okta-react';
import { useNavigate } from 'react-router-dom';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { APIProvider } from '@pella/cosmos-react';
import { CameraProvider } from '@pella/barcode-scanner';
import { toast } from '@pella/sash-framework';
import { MainLayout } from './components';
import { Constants, oktaAuth } from './data/appConfig';
import { FlagsmithProvider } from 'flagsmith/react';
import flagsmith from 'flagsmith';
import { I18nextProvider } from 'react-i18next';
import i18n from './translations';
import { LicenseInfo } from '@mui/x-license';

// Initialize the MUI license key for the DataGrid component
LicenseInfo.setLicenseKey(globalThis.REACT_APP_DATAGRID_KEY);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      toast.dismiss();
      toast.error(error.message);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      toast.dismiss();
      toast.error(error.message);
    },
  }),
});

/**
 * Structure taken straight from the Okta React React Router v6 sample
 * https://github.com/okta/okta-react/blob/master/samples/routing/react-router-dom-v6/src/App.tsx
 */
const App = () => {
  const navigate = useNavigate();
  const restoreOriginalUri = (_oktaAuth, originalUri) => {
    navigate(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <FlagsmithProvider
        options={{
          evaluationContext: {
            environment: {
              apiKey: window.REACT_APP_FLAGSMITH_ENVIRONMENT_API_KEY,
            },
          },
        }}
        flagsmith={flagsmith}
      >
        <I18nextProvider i18n={i18n}>
          <QueryClientProvider client={queryClient}>
            <APIProvider appName={Constants.AppName} baseURL={window.REACT_APP_API_URL}>
              <CameraProvider>
                <MainLayout />
              </CameraProvider>
            </APIProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </I18nextProvider>
      </FlagsmithProvider>
    </Security>
  );
};

export default App;
